document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

// Delegated header controls also work after Shopify replaces the header section.
const closeMenu = (menu, restoreFocus = false) => {
  menu.removeAttribute('open');
  if (restoreFocus) menu.querySelector('summary')?.focus();
};
document.addEventListener('toggle', (event) => {
  if (event.target.matches?.('.mobile-menu')) {
    document.body.classList.toggle('menu-open', Boolean(document.querySelector('.mobile-menu[open]')));
  }
}, true);
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.mobile-menu[open], .filter-drawer[open]').forEach((menu) => closeMenu(menu, true));
  document.body.classList.remove('menu-open');
});
document.addEventListener('click', (event) => {
  document.querySelectorAll('.mobile-menu[open]').forEach((menu) => {
    if (!menu.contains(event.target) || event.target.closest('nav a')) closeMenu(menu);
  });
  const close = event.target.closest('[data-filter-drawer-close]');
  if (close) closeMenu(close.closest('details'), true);
});
window.matchMedia('(min-width: 1181px)').addEventListener('change', (event) => {
  if (event.matches) document.querySelectorAll('.mobile-menu[open]').forEach((menu) => closeMenu(menu));
});
const sectionControllers = new WeakMap();
const initializeTheme = (root = document) => {
  sectionControllers.get(root)?.abort();
  const controller = new AbortController();
  sectionControllers.set(root, controller);
  const listen = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: controller.signal });
  root.querySelectorAll('[data-quantity-stepper]').forEach((stepper) => {
    const input = stepper.querySelector('input[type="number"]');
    if (!input) return;

    const updateButtons = () => {
      const value = Number(input.value || 0);
      const min = input.min === '' ? Number.NEGATIVE_INFINITY : Number(input.min);
      const max = input.max === '' ? Number.POSITIVE_INFINITY : Number(input.max);
      const decreaseButton = stepper.querySelector('[data-quantity-change="-1"]');
      const increaseButton = stepper.querySelector('[data-quantity-change="1"]');
      if (decreaseButton) decreaseButton.disabled = value <= min;
      if (increaseButton) increaseButton.disabled = value >= max;
    };

    stepper.querySelectorAll('[data-quantity-change]').forEach((button) => {
      listen(button, 'click', () => {
        const change = Number(button.dataset.quantityChange || 0);
        if (change > 0) input.stepUp();
        if (change < 0) input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        updateButtons();
      });
    });

    listen(input, 'input', updateButtons);
    listen(input, 'change', updateButtons);
    updateButtons();
  });

  root.querySelectorAll('[data-sort-select]').forEach((select) => {
    listen(select, 'change', () => {
      if (select.form?.requestSubmit) select.form.requestSubmit();
      else if (select.form?.checkValidity()) select.form.submit();
    });
  });

  const cartForm = root.querySelector('[data-cart-form]');
  const cartUpdateHint = root.querySelector('[data-cart-update-hint]');
  if (cartForm && cartUpdateHint) {
    listen(cartForm, 'change', (event) => {
      if (!event.target.matches('input[name="updates[]"]')) return;
      cartForm.classList.add('is-dirty');
      cartUpdateHint.textContent = 'Quantity changed. Select Update cart to refresh totals.';
    });
  }

  const trimTrailingEmptyContent = (container) => {
    while (container.lastChild) {
      const lastNode = container.lastChild;
      const isEmptyText = lastNode.nodeType === Node.TEXT_NODE
        && !lastNode.textContent.replace(/\u00a0/g, ' ').trim();
      const isLineBreak = lastNode.nodeType === Node.ELEMENT_NODE
        && lastNode.tagName === 'BR';

      if (isEmptyText || isLineBreak) {
        lastNode.remove();
        continue;
      }

      if (lastNode.nodeType === Node.ELEMENT_NODE) {
        trimTrailingEmptyContent(lastNode);
        const hasText = Boolean(lastNode.textContent.replace(/\u00a0/g, ' ').trim());
        const hasMediaOrControl = Boolean(lastNode.matches('img, video, iframe, svg, table, hr, input, button')
          || lastNode.querySelector('img, video, iframe, svg, table, hr, input, button'));
        if (!hasText && !hasMediaOrControl) {
          lastNode.remove();
          continue;
        }
      }

      break;
    }
  };

  root.querySelectorAll('[data-trim-trailing-space]').forEach(trimTrailingEmptyContent);

  const mobileProductGallery = window.matchMedia('(max-width: 760px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initializeProductGallery = (section) => {
    const gallery = section.querySelector('[data-product-gallery]');
    const slides = Array.from(section.querySelectorAll('[data-product-gallery-slide]'));
    const previousButton = section.querySelector('[data-gallery-previous]');
    const nextButton = section.querySelector('[data-gallery-next]');
    const currentLabel = section.querySelector('[data-gallery-current]');
    if (!gallery || slides.length === 0) return null;

    const thumbnails = Array.from(section.querySelectorAll('[data-gallery-index]'));
    section.classList.add('gallery-ready');
    let activeIndex = 0;
    let scrollFrame = 0;
    controller.signal.addEventListener('abort', () => window.cancelAnimationFrame(scrollFrame), { once: true });

    const updateGalleryState = (index) => {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));
      if (currentLabel) currentLabel.textContent = String(activeIndex + 1);
      if (previousButton) previousButton.disabled = activeIndex === 0;
      if (nextButton) nextButton.disabled = activeIndex === slides.length - 1;
      slides.forEach((slide, slideIndex) => {
        if (slideIndex === activeIndex) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
        slide.hidden = !mobileProductGallery.matches && slideIndex !== activeIndex;
        if (slideIndex !== activeIndex) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      thumbnails.forEach((thumb, index) => {
        if (index === activeIndex) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    };

    const scrollToSlide = (index, behavior = 'smooth') => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      const targetSlide = slides[nextIndex];
      updateGalleryState(nextIndex);
      if (!mobileProductGallery.matches) return;

      const galleryLeft = gallery.getBoundingClientRect().left;
      const slideLeft = targetSlide.getBoundingClientRect().left;
      const targetLeft = gallery.scrollLeft + slideLeft - galleryLeft;
      const scrollBehavior = reducedMotion.matches ? 'auto' : behavior;
      if (typeof gallery.scrollTo === 'function') gallery.scrollTo({ left: targetLeft, behavior: scrollBehavior });
      else gallery.scrollLeft = targetLeft;
    };

    thumbnails.forEach((thumb) => listen(thumb, 'click', () => scrollToSlide(Number(thumb.dataset.galleryIndex))));
    listen(previousButton, 'click', () => scrollToSlide(activeIndex - 1));
    listen(nextButton, 'click', () => scrollToSlide(activeIndex + 1));

    listen(gallery, 'keydown', (event) => {
      if (event.target !== gallery || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      scrollToSlide(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    listen(gallery, 'scroll', () => {
      if (!mobileProductGallery.matches) return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const galleryCenter = gallery.getBoundingClientRect().left + (gallery.clientWidth / 2);
        const closestIndex = slides.reduce((closest, slide, index) => {
          const slideBounds = slide.getBoundingClientRect();
          const distance = Math.abs((slideBounds.left + (slideBounds.width / 2)) - galleryCenter);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
        updateGalleryState(closestIndex);
      });
    }, { passive: true });

    listen(mobileProductGallery, 'change', (event) => {
      updateGalleryState(activeIndex);
      if (event.matches) scrollToSlide(activeIndex, 'auto');
      else if (typeof gallery.scrollTo === 'function') gallery.scrollTo({ left: 0, behavior: 'auto' });
      else gallery.scrollLeft = 0;
    });

    updateGalleryState(0);
    if (mobileProductGallery.matches) gallery.scrollLeft = 0;
    return {
      showMediaById(mediaId, behavior = 'smooth', imageId) {
        if (!mediaId && !imageId) return false;
        const mediaIndex = slides.findIndex((slide) => (mediaId && slide.dataset.productMediaId === String(mediaId))
          || (imageId && slide.dataset.productImageId === String(imageId)));
        if (mediaIndex < 0) return false;
        scrollToSlide(mediaIndex, behavior);
        return true;
      },
      reveal(behavior = 'smooth') {
        const bounds = gallery.getBoundingClientRect();
        if (bounds.bottom <= 110 || bounds.top >= window.innerHeight) {
          gallery.scrollIntoView({ block: 'start', behavior: reducedMotion.matches ? 'auto' : behavior });
        }
      }
    };
  };

  root.querySelectorAll('[data-product-section]').forEach((section) => {
    const galleryController = initializeProductGallery(section);
    const variantSelect = section.querySelector('select[data-variant-select]');
    const variantsElement = section.querySelector('[data-product-variants]');
    const optionGroups = Array.from(section.querySelectorAll('[data-product-option]'));
    const currentPrice = section.querySelector('[data-product-current-price]');
    const comparePrice = section.querySelector('[data-product-compare-price]');
    const submitButton = section.querySelector('.product-submit');
    const productStatus = section.querySelector('[data-product-status]');
    const selectionFeedback = section.querySelector('[data-selection-feedback]');
    if (!variantSelect) return;

    let variants = [];
    if (variantsElement) {
      try {
        const parsedVariants = JSON.parse(variantsElement.textContent || '[]');
        variants = Array.isArray(parsedVariants)
          ? parsedVariants.filter((variant) => variant && Array.isArray(variant.options))
          : [];
      } catch (error) {
        variants = [];
      }
    }

    const variantsById = new Map(variants.map((variant) => [String(variant.id), variant]));
    const mediaByVariantId = new Map(Array.from(variantSelect.options, (option) => [option.value, {
      id: option.dataset.mediaId,
      imageId: option.dataset.imageId
    }]));
    const colorGroupIndex = optionGroups.findIndex((group) => group.hasAttribute('data-color-option'));
    const selectedValues = () => optionGroups.map((group) => group.querySelector('[data-option-value]:checked')?.value || '');
    const colorInputs = colorGroupIndex < 0 ? [] : Array.from(optionGroups[colorGroupIndex].querySelectorAll('[data-option-value]'));
    const imageSlides = Array.from(section.querySelectorAll('[data-product-gallery-slide][data-product-media-type="image"]'));
    // Match color order to photo order. Videos and models do not count as photos.
    const mediaByColorPosition = new Map(colorInputs.slice(0, imageSlides.length).map((input, index) => [input.value, {
      id: imageSlides[index].dataset.productMediaId
    }]));
    const normalizeMediaLabel = (value) => String(value || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    const colorNames = [...new Set(colorInputs.map((input) => normalizeMediaLabel(input.value)).filter(Boolean))];
    const mediaByColorLabel = new Map();
    section.querySelectorAll('[data-product-gallery-slide]').forEach((slide) => {
      // Match explicit photo labels only. Ambiguous labels never select a guessed color.
      for (const label of [slide.dataset.productMediaAlt, slide.dataset.productMediaFilename]) {
        const normalizedLabel = ` ${normalizeMediaLabel(label)} `;
        const matches = colorNames.filter((color) => normalizedLabel.includes(` ${color} `));
        const specificMatches = matches.filter((color) => !matches.some((other) => other !== color && ` ${other} `.includes(` ${color} `)));
        if (specificMatches.length !== 1) continue;
        const color = specificMatches[0];
        if (!mediaByColorLabel.has(color)) mediaByColorLabel.set(color, { id: slide.dataset.productMediaId });
        break;
      }
    });

    // Use the color's position even when a variant has a different assigned photo.
    // If there are fewer photos than colors, retain the existing media fallbacks.
    const showSelectedMedia = (behavior = 'smooth', reveal = false) => {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const color = colorGroupIndex < 0 ? '' : selectedValues()[colorGroupIndex];
      const showMedia = (media) => {
        const shown = galleryController?.showMediaById(media?.id, behavior, media?.imageId);
        if (shown && reveal) galleryController.reveal(behavior);
        return shown;
      };
      if (showMedia(mediaByColorPosition.get(color))) return;
      if (showMedia(mediaByVariantId.get(selectedOption?.value))) return;
      if (!color) return;
      for (const variant of variants) {
        if (variant.options[colorGroupIndex] !== color) continue;
        if (showMedia(mediaByVariantId.get(String(variant.id)))) return;
      }
      showMedia(mediaByColorLabel.get(normalizeMediaLabel(color)));
    };

    const updateSelectedLabels = () => {
      optionGroups.forEach((group) => {
        const selectedInput = group.querySelector('[data-option-value]:checked');
        const selectedLabel = group.querySelector('[data-selected-option]');
        if (selectedInput && selectedLabel) selectedLabel.textContent = selectedInput.value;
      });
    };

    const updateOptionAvailability = () => {
      if (!variants.length) return;
      const values = selectedValues();

      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidateValues = [...values];
          candidateValues[groupIndex] = input.value;
          const hasAvailableVariant = variants.some((variant) => {
            if (!variant.available) return false;
            return variant.options.every((value, index) => !candidateValues[index] || candidateValues[index] === value);
          });
          const label = input.nextElementSibling;
          if (label?.classList.contains('product-option__label')) {
            label.classList.toggle('is-unavailable', !hasAvailableVariant);
          }
          // Allow crossing unavailable combinations to reach another color or size.
          input.disabled = false;
        });
      });
    };

    const updateProductState = (updateUrl = true) => {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      if (!selectedOption) return;

      const available = selectedOption.dataset.available === 'true';
      if (selectionFeedback) {
        selectionFeedback.hidden = available;
        selectionFeedback.textContent = available ? '' : 'This combination is sold out. Choose another color or size.';
      }
      section.querySelector('.product-dynamic-checkout')?.toggleAttribute('hidden', !available);
      const selectedComparePrice = selectedOption.dataset.comparePrice || '';

      if (currentPrice) currentPrice.textContent = selectedOption.dataset.price || '';
      if (comparePrice) {
        comparePrice.textContent = selectedComparePrice;
        comparePrice.hidden = !selectedComparePrice;
      }
      if (submitButton) {
        submitButton.disabled = !available;
        submitButton.textContent = available ? submitButton.dataset.addLabel : submitButton.dataset.soldOutLabel;
      }
      section.classList.toggle('is-product-unavailable', !available);

      if (productStatus) {
        productStatus.textContent = available ? productStatus.dataset.availableLabel : productStatus.dataset.soldOutLabel;
        productStatus.classList.toggle('is-sold-out', !available);
      }

      if (updateUrl) {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('variant', selectedOption.value);
        window.history.replaceState({}, '', nextUrl);
      }
    };

    optionGroups.forEach((group) => {
      // Re-selecting the current color restores its photo after browsing the gallery.
      if (group.hasAttribute('data-color-option')) {
        listen(group, 'click', (event) => {
          if (!event.target.matches('[data-option-value]:checked')) return;
          const variant = variantsById.get(variantSelect.value);
          const values = selectedValues();
          if (variantSelect.disabled || variant?.options.every((value, index) => value === values[index])) {
            showSelectedMedia('smooth', true);
          }
        });
      }
      listen(group, 'change', (event) => {
        if (!event.target.matches('[data-option-value]')) return;
        updateSelectedLabels();

        const values = selectedValues();
        const selectedVariant = variants.find((variant) => variant.options.every((value, index) => value === values[index]));
        if (selectedVariant) {
          variantSelect.disabled = false;
          variantSelect.value = String(selectedVariant.id);
          // Notify Shopify's payment controls as well as the theme.
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (submitButton) {
          if (selectionFeedback) {
            selectionFeedback.hidden = false;
            selectionFeedback.textContent = 'This combination is unavailable. Choose another color or size.';
          }
          variantSelect.selectedIndex = -1;
          variantSelect.disabled = true;
          section.querySelector('.product-dynamic-checkout')?.setAttribute('hidden', '');
          submitButton.disabled = true;
          submitButton.textContent = submitButton.dataset.soldOutLabel;
          section.classList.add('is-product-unavailable');
          if (productStatus) {
            productStatus.textContent = productStatus.dataset.soldOutLabel;
            productStatus.classList.add('is-sold-out');
          }
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.delete('variant');
          window.history.replaceState({}, '', nextUrl);
          showSelectedMedia('smooth', group.hasAttribute('data-color-option'));
        }
        if (selectedVariant && group.hasAttribute('data-color-option')) showSelectedMedia('smooth', true);
        updateOptionAvailability();
      });
    });

    listen(variantSelect, 'change', () => {
      const variant = variantsById.get(variantSelect.value);
      if (variant) {
        optionGroups.forEach((group, index) => {
          group.querySelectorAll('[data-option-value]').forEach((input) => {
            input.checked = input.value === variant.options[index];
          });
        });
      }
      updateSelectedLabels();
      updateOptionAvailability();
      updateProductState(true);
      showSelectedMedia();
    });
    updateSelectedLabels();
    updateOptionAvailability();
    updateProductState(false);
    // Normal product links start at the main image. Explicit variant links show that color.
    if (new URL(window.location.href).searchParams.get('variant') === variantSelect.value) showSelectedMedia('auto');
    if (variants.length && optionGroups.length) section.classList.add('variants-ready');
  });



  root.querySelectorAll('[data-rotate-announcement]').forEach((bar) => {
    const messages = Array.from(bar.querySelectorAll('.announcement-bar__message'));
    if (messages.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const interval = Number(bar.dataset.interval || 4000);
    let index = 0;

    messages.forEach((message, messageIndex) => {
      message.classList.toggle('is-active', messageIndex === 0);
      message.setAttribute('aria-hidden', messageIndex === 0 ? 'false' : 'true');
    });

    const timer = window.setInterval(() => {
      if (!bar.isConnected) { window.clearInterval(timer); return; }
      if (document.hidden || bar.matches(':hover') || bar.contains(document.activeElement)) return;
      const current = messages[index];
      index = (index + 1) % messages.length;
      const next = messages[index];

      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      next.classList.add('is-active');
      next.setAttribute('aria-hidden', 'false');
    }, Math.max(3000, interval));
    controller.signal.addEventListener('abort', () => window.clearInterval(timer), { once: true });
  });

  root.querySelectorAll('[data-product-recommendations]').forEach((section) => {
    const url = section.dataset.url;
    if (!url || section.innerHTML.trim()) return;
    fetch(url, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error('Recommendations unavailable'); return response.text(); })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const replacement = html.querySelector('[data-product-recommendations]');
        if (replacement && replacement.innerHTML.trim()) {
          section.innerHTML = replacement.innerHTML;
          section.hidden = false;
        } else {
          section.hidden = true;
        }
      })
      .catch(() => {
        section.hidden = true;
      });
  });
  root.querySelectorAll('select[data-default]').forEach((select) => {
    if (select.dataset.default) select.value = select.dataset.default;
  });
};
const initializePage = () => {
  const sections = document.querySelectorAll('.shopify-section');
  if (sections.length) sections.forEach((section) => initializeTheme(section));
  else initializeTheme();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePage);
else initializePage();
document.addEventListener('shopify:section:load', (event) => initializeTheme(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  sectionControllers.get(event.target)?.abort();
  if (event.target.querySelector('.mobile-menu')) document.body.classList.remove('menu-open');
});
